import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Target, CheckCircle2, XCircle, ArrowLeft, Trophy, RotateCcw, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TargetedPracticePage() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [correct, setCorrect] = useState(0);
  const [wrong, setWrong] = useState(0);
  const [finished, setFinished] = useState(false);
  const [mastered, setMastered] = useState(false);
  const [masteryCount, setMasteryCount] = useState(0);
  const [xpEarned, setXpEarned] = useState(0);
  const [newBadges, setNewBadges] = useState([]);

  useEffect(() => {
    axios.get(`${API}/user/weak-questions`)
      .then(res => setQuestions(res.data.questions || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const total = questions.length;
  const q = questions[current];
  const progress = total > 0 ? ((current + (showFeedback ? 1 : 0)) / total) * 100 : 0;

  const handleAnswer = (option) => {
    if (showFeedback) return;
    setSelected(option);
    setShowFeedback(true);
    const isCorrect = option === q.correct_answer;
    if (isCorrect) setCorrect(c => c + 1);
    else setWrong(w => w + 1);

    // Record attempt so mastered questions get cleared from weak list (2 correct = clear)
    axios.post(`${API}/practice/record`, {
      cocktail_id: q.cocktail_id,
      question_index: q.question_index,
      correct: isCorrect,
    }).then(res => {
      setMasteryCount(res.data.correct_count || 0);
      setMastered(!!res.data.mastered);
      setXpEarned(res.data.xp_earned || 0);
      setNewBadges(res.data.new_badges || []);
    }).catch(() => {});
  };

  const handleNext = () => {
    if (current + 1 >= total) { setFinished(true); return; }
    setCurrent(c => c + 1);
    setSelected(null);
    setShowFeedback(false);
    setMastered(false);
    setMasteryCount(0);
    setXpEarned(0);
    setNewBadges([]);
  };

  const handleRestart = () => {
    setLoading(true);
    axios.get(`${API}/user/weak-questions`)
      .then(res => {
        setQuestions(res.data.questions || []);
        setCurrent(0); setSelected(null); setShowFeedback(false);
        setCorrect(0); setWrong(0); setFinished(false);
        setMastered(false); setMasteryCount(0);
      })
      .finally(() => setLoading(false));
  };

  if (loading) return (
    <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
      <p className="text-[hsl(var(--muted-foreground))]">Loading weak areas...</p>
    </div>
  );

  if (total === 0) return (
    <div className="min-h-screen bg-[hsl(var(--background))] flex flex-col items-center justify-center gap-4 p-4">
      <CheckCircle2 size={48} className="text-green-400" />
      <h2 className="text-xl font-bold">No Weak Areas Found!</h2>
      <p className="text-[hsl(var(--muted-foreground))] text-center text-sm">
        You've answered all your test questions correctly, or you haven't taken any tests yet.
      </p>
      <Button onClick={() => navigate(-1)} variant="outline" data-testid="practice-back-empty">Back to Dashboard</Button>
    </div>
  );

  const pct = total > 0 ? Math.round((correct / total) * 100) : 0;

  if (finished) return (
    <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center p-4">
      <div className="max-w-lg mx-auto">
        <div className="rounded-xl border border-[hsl(var(--border))] overflow-hidden bg-[hsl(var(--card))]">
          <div className="px-4 py-3 text-center bg-[hsl(var(--secondary))]">
            <span className="text-sm font-bold uppercase tracking-widest text-[hsl(var(--primary))]">Practice Complete</span>
          </div>
          <div className="p-6 space-y-6 text-center">
            <Trophy size={48} className="mx-auto text-[hsl(var(--primary))]" />
            <div>
              <p className="text-4xl font-bold text-[hsl(var(--foreground))]" data-testid="practice-final-score">{pct}%</p>
              <p className="text-sm mt-1 text-[hsl(var(--muted-foreground))]">of your weak areas</p>
            </div>
            <div className="flex justify-center gap-8">
              <div className="text-center"><p className="text-2xl font-bold text-green-400">{correct}</p><p className="text-xs text-[hsl(var(--muted-foreground))]">Now correct</p></div>
              <div className="text-center"><p className="text-2xl font-bold text-red-400">{wrong}</p><p className="text-xs text-[hsl(var(--muted-foreground))]">Still weak</p></div>
              <div className="text-center"><p className="text-2xl font-bold">{total}</p><p className="text-xs text-[hsl(var(--muted-foreground))]">Total</p></div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button onClick={handleRestart} className="flex-1 bg-[hsl(var(--primary))] text-black" data-testid="practice-restart">
                <RotateCcw size={16} className="mr-2" /> Practice Again
              </Button>
              <Button variant="outline" onClick={() => navigate(-1)} className="flex-1" data-testid="practice-back-done">
                <ArrowLeft size={16} className="mr-2" /> Dashboard
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] p-4">
      <div className="max-w-lg mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-[hsl(var(--muted-foreground))]" data-testid="practice-back">
            <ArrowLeft size={16} /> Back
          </button>
          <div className="flex items-center gap-2">
            <Target size={16} className="text-[hsl(var(--primary))]" />
            <span className="text-sm font-bold text-[hsl(var(--primary))]">Weak Areas</span>
          </div>
          <span className="text-xs text-[hsl(var(--muted-foreground))]">{current + 1}/{total}</span>
        </div>

        <Progress value={progress} className="h-1.5 bg-[hsl(var(--secondary))]" />

        <div className="flex justify-center gap-6 text-xs">
          <span className="flex items-center gap-1 text-green-400"><CheckCircle2 size={14} /> {correct}</span>
          <span className="flex items-center gap-1 text-red-400"><XCircle size={14} /> {wrong}</span>
        </div>

        {/* Question */}
        <div className="rounded-xl border border-[hsl(var(--border))] overflow-hidden bg-[hsl(var(--card))]" data-testid={`practice-question-${current}`}>
          <div className="px-4 py-3 border-b border-[hsl(var(--border))] bg-[hsl(var(--secondary))]">
            <p className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--primary))]">{q.type}</p>
            <p className="text-xs mt-0.5 text-[hsl(var(--muted-foreground))]">{q.cocktail_name}</p>
          </div>
          <div className="p-4">
            {/* Show what they previously answered wrong */}
            <div className="mb-3 px-3 py-2 rounded-md bg-red-500/5 border border-red-500/10">
              <p className="text-[10px] uppercase text-red-400 font-bold">You previously answered:</p>
              <p className="text-xs text-red-300 mt-0.5">{q.your_wrong_answer}</p>
            </div>

            <p className="text-sm font-medium mb-4">{q.question_text}</p>
            <div className="space-y-2">
              {q.options.map((opt, i) => {
                let cls = "border-[hsl(var(--border))] bg-[hsl(var(--secondary))] hover:border-[hsl(var(--primary))]/50";
                if (showFeedback) {
                  if (opt === q.correct_answer) cls = "border-green-500 bg-green-500/10 text-green-400";
                  else if (opt === selected && opt !== q.correct_answer) cls = "border-red-500 bg-red-500/10 text-red-400";
                }
                return (
                  <button key={i} onClick={() => handleAnswer(opt)} disabled={showFeedback}
                    className={`w-full text-left px-4 py-3 rounded-lg border text-sm transition-all ${cls}`}
                    data-testid={`practice-option-${i}`}
                  >{opt}</button>
                );
              })}
            </div>
          </div>

          {showFeedback && (
            <div className="px-4 pb-4">
              <div className="flex items-center gap-2 mb-3">
                {selected === q.correct_answer
                  ? <><CheckCircle2 size={18} className="text-green-400" /><span className="text-green-400 text-sm font-bold">Got it this time!</span></>
                  : <><XCircle size={18} className="text-red-400" /><span className="text-red-400 text-sm font-bold">Still needs work</span></>}
              </div>
              {selected === q.correct_answer && (
                mastered ? (
                  <div className="mb-3 px-3 py-2 rounded-md bg-[hsl(var(--primary))]/10 border border-[hsl(var(--primary))]/30 space-y-1.5" data-testid="practice-mastered-banner">
                    <div className="flex items-center gap-2">
                      <Zap size={14} className="text-[hsl(var(--primary))]" />
                      <p className="text-xs text-[hsl(var(--primary))] font-bold">Mastered! Cleared from your weak list.</p>
                      {xpEarned > 0 && (
                        <span className="ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded bg-[hsl(var(--primary))] text-black" data-testid="practice-mastered-xp">+{xpEarned} XP</span>
                      )}
                    </div>
                    {newBadges.length > 0 && (
                      <div className="flex flex-wrap gap-1 pt-1" data-testid="practice-mastered-badges">
                        {newBadges.map(b => (
                          <span key={b.badge_id} className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-yellow-500/20 border border-yellow-500/40 text-yellow-300">
                            New Badge: {b.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="mb-3 px-3 py-2 rounded-md bg-green-500/5 border border-green-500/10" data-testid="practice-mastery-progress">
                    <p className="text-[10px] uppercase text-green-400 font-bold">Mastery progress</p>
                    <p className="text-xs text-green-300 mt-0.5">{masteryCount}/2 correct — {2 - masteryCount} more to clear this question</p>
                  </div>
                )
              )}
              <Button onClick={handleNext} className="w-full font-bold bg-[hsl(var(--primary))] text-black" data-testid="practice-next">
                {current + 1 >= total ? "See Results" : "Next Question"}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
